import { motion, useReducedMotion } from "framer-motion";
import type { ReactNode } from "react";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

/**
 * Animated progress ring. `value` 0..1, or null for an indeterminate spinning arc (static under reduced motion).
 * Purely decorative: the caller exposes progress with a role="progressbar" element or text.
 */
export function ProgressRing({ value, size = 36, stroke = 3.5, color = "var(--accent)", className, children, glow }: { value: number | null; size?: number; stroke?: number; color?: string; className?: string; children?: ReactNode; glow?: boolean }) {
  const reduce = useReducedMotion();
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const v = value === null ? 0.28 : Math.max(0, Math.min(1, value));
  return (
    <span className={cn("relative inline-grid shrink-0 place-items-center", className)} style={{ width: size, height: size }} aria-hidden>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={cn("-rotate-90", value === null && !reduce && "animate-spin [animation-duration:1.2s]")}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface-3)" strokeWidth={stroke} />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          initial={false}
          animate={{ strokeDashoffset: c * (1 - v) }}
          transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 90, damping: 20 }}
          style={glow && !reduce ? { filter: `drop-shadow(0 0 3px ${color})` } : undefined}
        />
      </svg>
      {children && <span className="absolute inset-0 grid place-items-center">{children}</span>}
    </span>
  );
}

/** Thin animated bar with a moving sheen while running (sheen off under reduced motion). */
export function ProgressBar({ value, color = "var(--accent)", running, className }: { value: number | null; color?: string; running?: boolean; className?: string }) {
  const reduce = useReducedMotion();
  const sign = useT().rtl ? -1 : 1;
  const v = value === null ? null : Math.max(0, Math.min(1, value));
  return (
    <div className={cn("relative h-1.5 w-full overflow-hidden rounded-full bg-surface-3", className)} aria-hidden>
      {v === null ? (
        <motion.div
          className="absolute inset-y-0 start-0 w-1/3 rounded-full"
          style={{ background: color }}
          initial={{ x: reduce ? `${100 * sign}%` : `${-100 * sign}%` }}
          animate={reduce ? { x: `${100 * sign}%` } : { x: [`${-100 * sign}%`, `${300 * sign}%`] }}
          transition={reduce ? { duration: 0 } : { duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
        />
      ) : (
        <motion.div
          className="absolute inset-y-0 start-0 rounded-full"
          style={{ background: color }}
          initial={false}
          animate={{ width: `${v * 100}%` }}
          transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 80, damping: 20 }}
        >
          {running && !reduce && (
            <span className="absolute inset-0 overflow-hidden rounded-full">
              <span className="absolute inset-0 -translate-x-full animate-shimmer bg-[linear-gradient(90deg,transparent,color-mix(in_srgb,white_55%,transparent),transparent)] rtl:translate-x-full rtl:[animation-direction:reverse]" />
            </span>
          )}
        </motion.div>
      )}
    </div>
  );
}
